import React, { useState } from 'react';
import { ShieldCheck, Search, Filter, Download, Eye, CheckCircle2, XCircle, Clock, FileText, AlertCircle, AlertTriangle, ExternalLink, Brain, HelpCircle, X } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
interface AdminVerificationManagerProps {
  verificationQueue: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminVerificationManager({
  verificationQueue,
  astrologers,
  setActiveTab,
  setDetailPanel,
  success
}: AdminVerificationManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    docType: 'all',
    language: 'all',
    country: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [viewingDoc, setViewingDoc] = useState<{
    type: string;
    lines: string[];
    icon: string;
    bg: string;
  } | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});
  // --- MOCK DATA ENRICHMENT ---
  const enrichedQueue = verificationQueue.map((v, i) => {
    const isRisky = i % 5 === 0;
    const isFast = i % 3 === 0;
    const isLong = i % 4 === 0;
    const itemId = v.id || `800${i}`;
    return {
      ...v,
      id: itemId,
      status: statusOverrides[itemId] || v.status || (i % 4 === 0 ? 'needs_info' : 'pending'),
      docTypes: ['Паспорт', i % 2 === 0 ? 'Диплом' : 'Сертификат', i % 3 === 0 ? 'Видео-презентация' : null].filter(Boolean),
      lastAction: i % 2 === 0 ? 'Астролог загрузил документ' : 'Админ запросил уточнение',
      moderator: i % 3 === 0 ? 'Система' : 'Админ #1',
      waitTime: isFast ? '2 часа' : isLong ? '3 дня' : '1 день',
      waitStatus: isFast ? 'good' : isLong ? 'critical' : 'warning',
      aiConfidence: 85 + i % 15,
      riskIndicators: isRisky ? ['повторная заявка', 'подозрительный IP'] : [],
      history: [{
        date: '12 Окт 10:00',
        text: 'Заявка создана',
        actor: 'Астролог'
      }, {
        date: '13 Окт 14:30',
        text: 'Загружен диплом',
        actor: 'Астролог'
      }, ...(i % 2 !== 0 ? [{
        date: '14 Окт 09:15',
        text: 'Запрошено уточнение по паспорту',
        actor: 'Админ #1'
      }] : []), ...(i % 2 !== 0 ? [{
        date: '15 Окт 11:20',
        text: 'Документ обновлён',
        actor: 'Астролог'
      }] : [])]
    };
  });
  // KPI Calculations
  const pendingReview = enrichedQueue.filter(v => v.status === 'pending').length;
  const needsMoreInfo = enrichedQueue.filter(v => v.status === 'needs_info').length;
  const approvedCount = enrichedQueue.filter(v => v.status === 'approved').length + 12;
  const rejectedCount = enrichedQueue.filter(v => v.status === 'rejected').length + 3;
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, _textClass: string, statusFilter: string) => <Card padding="sm" hoverable className={`rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group transition-all ${filters.status === statusFilter ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''}`} onClick={() => setFilters({
    ...filters,
    status: statusFilter
  })} data-id="element-7495">
      <div className="flex justify-between items-start" data-id="element-7496">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-7497">
          {icon}
        </div>
      </div>
      <div data-id="element-7498">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-7499">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-7500">{value}</div>
      </div>
    </Card>;
  const handleExport = () => {
    success(`Экспорт ${filteredQueue.length} заявок завершён`);
  };
  const handleApprove = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'approved'
    }));
    success(`✅ Заявка ${request.astrologer || request.name} одобрена`);
  };
  const handleReject = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'rejected'
    }));
    success(`❌ Заявка ${request.astrologer || request.name} отклонена`);
  };
  const handleRequestInfo = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'needs_info'
    }));
    success(`📋 Запрошена дополнительная информация у ${request.astrologer || request.name}`);
  };
  // Bulk Actions
  const toggleAll = () => {
    if (selectedIds.size === currentQueue.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentQueue.map(v => v.id)));
    }
  };
  const toggleOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleBulkApprove = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'approved';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`✅ Одобрено заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  const handleBulkReject = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'rejected';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`❌ Отклонено заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  const handleBulkRequestInfo = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'needs_info';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`📋 Запрошена информация для заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  // Filtering
  const filteredQueue = enrichedQueue.filter(v => {
    const matchesSearch = (v.astrologer || v.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filters.status !== 'all' && v.status !== filters.status) return false;
    if (filters.docType !== 'all' && !v.docTypes.includes(filters.docType)) return false;
    return true;
  });
  const totalPages = Math.ceil(filteredQueue.length / itemsPerPage);
  const currentQueue = filteredQueue.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="space-y-6 relative" data-id="element-7501">
      <div className="flex justify-between items-center" data-id="element-7502">
        <div data-id="element-7503">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-7504">Верификация</h2>
          <p className="text-sm text-slate-500" data-id="element-7505">
            Проверка документов и квалификации астрологов
          </p>
        </div>
      </div>

      {/* KPI Cards (Clickable) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-7506">
        {renderKpiCard('Все заявки', enrichedQueue.length, <FileText className="w-4 h-4 text-slate-600" data-id="element-7507" />, 'bg-slate-100', 'text-slate-600', 'all')}
        {renderKpiCard('На проверке', pendingReview, <Clock className="w-4 h-4 text-amber-600" data-id="element-7508" />, 'bg-amber-50', 'text-amber-600', 'pending')}
        {renderKpiCard('Нужно дополнить', needsMoreInfo, <AlertCircle className="w-4 h-4 text-orange-600" data-id="element-7509" />, 'bg-orange-50', 'text-orange-600', 'needs_info')}
        {renderKpiCard('Одобрено', approvedCount, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-7510" />, 'bg-emerald-50', 'text-emerald-600', 'approved')}
        {renderKpiCard('Отклонено', rejectedCount, <XCircle className="w-4 h-4 text-rose-600" data-id="element-7511" />, 'bg-rose-50', 'text-rose-600', 'rejected')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-7512">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-7513">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-7514">
            <div className="relative flex-1 max-w-md" data-id="element-7515">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-7516" />
              <input type="text" placeholder="Поиск по имени астролога, ID заявки..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7517" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-7519" />} onClick={() => setShowFilters(!showFilters)} data-id="element-7518">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-7520">
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-7522" />} onClick={handleExport} data-id="element-7521">
              Экспорт CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-7523">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-7524">
              <div data-id="element-7525">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7526">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-7527">
                  <option value="all" data-id="element-7528">Все</option>
                  <option value="pending" data-id="element-7529">На проверке</option>
                  <option value="needs_info" data-id="element-7530">Нужно дополнить</option>
                  <option value="approved" data-id="element-7531">Одобрено</option>
                  <option value="rejected" data-id="element-7532">Отклонено</option>
                </select>
              </div>
              <div data-id="element-7533">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7534">
                  Тип документа
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.docType} onChange={e => setFilters({
              ...filters,
              docType: e.target.value
            })} data-id="element-7535">
                  <option value="all" data-id="element-7536">Все типы</option>
                  <option value="Паспорт" data-id="element-7537">Паспорт</option>
                  <option value="Диплом" data-id="element-7538">Диплом</option>
                  <option value="Сертификат" data-id="element-7539">Сертификат</option>
                  <option value="Видео-презентация" data-id="element-7540">Видео-презентация</option>
                </select>
              </div>
              <div data-id="element-7541">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7542">
                  Язык
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.language} onChange={e => setFilters({
              ...filters,
              language: e.target.value
            })} data-id="element-7543">
                  <option value="all" data-id="element-7544">Все языки</option>
                  <option value="ru" data-id="element-7545">Русский</option>
                  <option value="en" data-id="element-7546">Английский</option>
                </select>
              </div>
              <div className="flex items-end" data-id="element-7547">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              docType: 'all',
              language: 'all',
              country: 'all'
            })} data-id="element-7548">
                  Сбросить фильтры
                </Button>
              </div>
            </div>
          </div>}
      </Card>

      {/* Data Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-7549">
        <div className="overflow-x-auto" data-id="element-7550">
          <table className="w-full text-left whitespace-nowrap" data-id="element-7551">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-7552">
              <tr data-id="element-7553">
                <th className="px-4 py-3 w-10" data-id="element-7554">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === currentQueue.length && currentQueue.length > 0} onChange={toggleAll} data-id="element-7555" />
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-7556">ID / Ожидание</th>
                <th className="px-4 py-3 font-medium" data-id="element-7557">Астролог</th>
                <th className="px-4 py-3 font-medium" data-id="element-7558">Тип документов</th>
                <th className="px-4 py-3 font-medium" data-id="element-7559">AI-проверка</th>
                <th className="px-4 py-3 font-medium" data-id="element-7560">Последнее действие</th>
                <th className="px-4 py-3 font-medium" data-id="element-7561">Модератор</th>
                <th className="px-4 py-3 font-medium" data-id="element-7562">Статус</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-7563">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-7564">
              {currentQueue.length === 0 ? <tr data-id="element-7565">
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500 text-sm" data-id="element-7566">
                    Очередь верификации пуста
                  </td>
                </tr> : currentQueue.map(v => <tr key={v.id} className={`hover:bg-slate-50 transition-colors group ${selectedIds.has(v.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-7567">
                    <td className="px-4 py-3" data-id="element-7568">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(v.id)} onChange={() => toggleOne(v.id)} data-id="element-7569" />
                    </td>
                    <td className="px-4 py-3" data-id="element-7570">
                      <div className="text-xs font-medium text-slate-900" data-id="element-7571">
                        VRF-{v.id}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1" data-id="element-7572">
                        <span className={`w-2 h-2 rounded-full ${v.waitStatus === 'good' ? 'bg-emerald-500' : v.waitStatus === 'warning' ? 'bg-amber-500' : 'bg-rose-500 animate-pulse'}`} data-id="element-7573" />
                        <span className="text-[10px] text-slate-500" data-id="element-7574">
                          {v.waitTime}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7575">
                      <div className="flex items-center gap-2" data-id="element-7576">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs flex-shrink-0" data-id="element-7577">
                          {(v.astrologer || v.name || '').split(' ').map((n: string) => n[0]).join('')}
                        </div>
                        <div data-id="element-7578">
                          <div className="text-sm font-medium text-indigo-600 hover:underline cursor-pointer flex items-center gap-1" onClick={() => {
                      const match = astrologers.find(a => a.name === (v.astrologer || v.name));
                      if (match) {
                        setDetailPanel({
                          type: 'astrologer_profile',
                          title: `Профиль: ${match.name}`,
                          data: match
                        });
                      }
                      setActiveTab('astrologers');
                    }} data-id="element-7579">
                            {v.astrologer || v.name}
                            <ExternalLink className="w-3 h-3 opacity-50" data-id="element-7580" />
                          </div>
                          {v.riskIndicators.length > 0 && <div className="flex items-center gap-1 mt-0.5 text-[10px] text-rose-600 font-medium" title={v.riskIndicators.join(', ')} data-id="element-7581">
                              <AlertTriangle className="w-3 h-3" data-id="element-7582" />
                              Риск: {v.riskIndicators[0]}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7583">
                      <div className="flex flex-wrap gap-1 max-w-[150px]" data-id="element-7584">
                        {v.docTypes.map((type: string, idx: number) => <Badge key={idx} variant="neutral" size="sm" className="text-[9px] px-1.5 py-0" data-id="element-7585">
                            {type}
                          </Badge>)}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7586">
                      <Badge variant={v.aiConfidence > 90 ? 'success' : 'warning'} size="sm" className="flex items-center gap-1 w-fit" data-id="element-7587">
                        <Brain className="w-3 h-3" data-id="element-7588" />
                        {v.aiConfidence}%
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-7589">
                      <div className="text-xs text-slate-700 max-w-[150px] truncate" title={v.lastAction} data-id="element-7590">
                        {v.lastAction}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7591">
                      <div className="text-xs text-slate-600 flex items-center gap-1.5" data-id="element-7592">
                        {v.moderator === 'Система' ? <Brain className="w-3 h-3 text-slate-400" data-id="element-7593" /> : <ShieldCheck className="w-3 h-3 text-slate-400" data-id="element-7594" />}
                        {v.moderator}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7595">
                      <Badge variant={v.status === 'approved' ? 'success' : v.status === 'rejected' ? 'error' : v.status === 'needs_info' ? 'warning' : 'default'} size="sm" data-id="element-7596">
                        {v.status === 'approved' ? 'Одобрено' : v.status === 'rejected' ? 'Отклонено' : v.status === 'needs_info' ? 'Нужно дополнить' : 'На проверке'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-7597">
                      <div className="flex items-center justify-end gap-1" data-id="element-7598">
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" icon={<Eye className="w-4 h-4" data-id="element-7600" />} onClick={() => setPreviewData(v)} title="Быстрый просмотр" data-id="element-7599" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-emerald-600 hover:bg-emerald-50" icon={<CheckCircle2 className="w-4 h-4" data-id="element-7602" />} onClick={() => handleApprove(v)} title="Одобрить" data-id="element-7601" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-orange-500 hover:bg-orange-50" icon={<HelpCircle className="w-4 h-4" data-id="element-7604" />} onClick={() => handleRequestInfo(v)} title="Запросить доп. информацию" data-id="element-7603" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-rose-600 hover:bg-rose-50" icon={<XCircle className="w-4 h-4" data-id="element-7606" />} onClick={() => handleReject(v)} title="Отклонить" data-id="element-7605" />
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-7607">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-7608" />
          </div>}
      </Card>

      {/* Floating Bulk Actions Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-40 animate-in slide-in-from-bottom-10 fade-in" data-id="element-7609">
          <span className="font-medium text-sm" data-id="element-7610">
            {selectedIds.size} выбрано
          </span>
          <div className="w-px h-6 bg-slate-700" data-id="element-7611" />
          <Button size="sm" variant="primary" onClick={handleBulkApprove} data-id="element-7612">
            Одобрить
          </Button>
          <Button size="sm" variant="outline" className="text-white border-slate-600 hover:bg-slate-800" onClick={handleBulkRequestInfo} data-id="element-7613">
            Запросить доп.
          </Button>
          <Button size="sm" variant="outline" className="text-rose-400 border-rose-800 hover:bg-rose-950 hover:text-rose-300" onClick={handleBulkReject} data-id="element-7614">
            Отклонить
          </Button>
        </div>}

      {/* Document Viewer Modal */}
      {viewingDoc && <div className="fixed inset-0 bg-slate-900/70 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={e => {
      if (e.target === e.currentTarget) setViewingDoc(null);
    }} data-id="element-7615">
          <Card className="w-full max-w-lg shadow-2xl overflow-hidden" padding="none" data-id="element-7616">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-7617">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-7618">
                <span className="text-2xl" data-id="element-7619">{viewingDoc.icon}</span>
                {viewingDoc.type}
              </h3>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-full" onClick={() => setViewingDoc(null)} data-id="element-7620">
                <X className="w-5 h-5" data-id="element-7621" />
              </Button>
            </div>
            <div className={`${viewingDoc.bg} p-8 flex flex-col items-center justify-center min-h-[400px]`} data-id="element-7622">
              <div className="text-6xl mb-6" data-id="element-7623">{viewingDoc.icon}</div>
              <div className="w-full max-w-xs space-y-2 bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm" data-id="element-7624">
                {viewingDoc.lines.map((line, li) => <div key={li} className="text-sm text-slate-600 text-center font-mono leading-relaxed" data-id="element-7625">
                    {line}
                  </div>)}
              </div>
              <p className="text-xs text-slate-400 mt-4" data-id="element-7626">
                {viewingDoc.type === 'Видео-презентация' ? 'MP4 · 48.2 MB' : 'PDF · 2.4 MB'}{' '}
                · Загружен 13 Окт 2024
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 bg-white flex justify-end gap-2" data-id="element-7627">
              <Button variant="outline" size="sm" onClick={() => setViewingDoc(null)} data-id="element-7628">
                Закрыть
              </Button>
              <Button variant="primary" size="sm" icon={<Download className="w-4 h-4" data-id="element-7630" />} data-id="element-7629">
                Скачать оригинал
              </Button>
            </div>
          </Card>
        </div>}

      {/* Quick Preview Modal */}
      {previewData && <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={e => {
      if (e.target === e.currentTarget) setPreviewData(null);
    }} data-id="element-7631">
          <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" padding="none" data-id="element-7632">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-7633">
              <div className="flex items-center gap-3" data-id="element-7634">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-7635">
                  Документы астролога
                </h3>
                <Badge variant="neutral" data-id="element-7636">VRF-{previewData.id}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-full" onClick={() => setPreviewData(null)} data-id="element-7637">
                <X className="w-5 h-5" data-id="element-7638" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 flex flex-col md:flex-row gap-6" data-id="element-7639">
              {/* Left Col: Documents & AI Check */}
              <div className="flex-1 space-y-6" data-id="element-7640">
                {/* AI & Risk Check */}
                <div className="grid grid-cols-2 gap-4" data-id="element-7641">
                  <Card padding="sm" className="border-indigo-100 bg-indigo-50/50" data-id="element-7642">
                    <div className="flex items-center gap-2 mb-2 text-indigo-900 font-semibold text-sm" data-id="element-7643">
                      <Brain className="w-4 h-4" data-id="element-7644" /> AI Document Check
                    </div>
                    <div className="space-y-2 text-xs text-indigo-800" data-id="element-7645">
                      <div className="flex justify-between" data-id="element-7646">
                        <span data-id="element-7647">Подлинность:</span>
                        <span className="font-bold" data-id="element-7648">
                          {previewData.aiConfidence}%
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-7649">
                        <span data-id="element-7650">Совпадение имени:</span>
                        <span className="font-bold text-emerald-600" data-id="element-7651">Да</span>
                      </div>
                      <div className="flex justify-between" data-id="element-7652">
                        <span data-id="element-7653">Текст сертификатов:</span>
                        <span className="font-bold text-emerald-600" data-id="element-7654">OK</span>
                      </div>
                    </div>
                  </Card>
                  <Card padding="sm" className={previewData.riskIndicators.length > 0 ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'} data-id="element-7655">
                    <div className={`flex items-center gap-2 mb-2 font-semibold text-sm ${previewData.riskIndicators.length > 0 ? 'text-rose-900' : 'text-emerald-900'}`} data-id="element-7656">
                      <ShieldCheck className="w-4 h-4" data-id="element-7657" /> Risk-контроль
                    </div>
                    {previewData.riskIndicators.length > 0 ? <ul className="text-xs text-rose-700 list-disc list-inside space-y-1" data-id="element-7658">
                        {previewData.riskIndicators.map((r: string, idx: number) => <li key={idx} data-id="element-7659">{r}</li>)}
                      </ul> : <div className="text-xs text-emerald-700" data-id="element-7660">
                        Подозрительных факторов не обнаружено
                      </div>}
                  </Card>
                </div>

                {/* Documents Grid */}
                <div data-id="element-7661">
                  <h4 className="font-semibold text-slate-900 mb-3 text-sm" data-id="element-7662">
                    Загруженные файлы
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-7663">
                    {previewData.docTypes.map((type: string, idx: number) => {
                  const docStyles: Record<string, {
                    bg: string;
                    border: string;
                    icon: string;
                    lines: string[];
                  }> = {
                    Паспорт: {
                      bg: 'bg-rose-50',
                      border: 'border-rose-200',
                      icon: '🪪',
                      lines: ['ПАСПОРТ', 'Российская Федерация', '━━━━━━━━━━━━━━', 'Фамилия: ██████████', 'Имя: ████████', 'Дата рождения: ██.██.████', '━━━━━━━━━━━━━━', 'Серия: ████  №: ██████']
                    },
                    Диплом: {
                      bg: 'bg-blue-50',
                      border: 'border-blue-200',
                      icon: '🎓',
                      lines: ['ДИПЛОМ', 'о профессиональной переподготовке', '━━━━━━━━━━━━━━', '████████████████', 'Специальность:', 'Астрология и консультирование', '━━━━━━━━━━━━━━', 'Рег. №: ██████-████']
                    },
                    Сертификат: {
                      bg: 'bg-amber-50',
                      border: 'border-amber-200',
                      icon: '📜',
                      lines: ['СЕРТИФИКАТ', 'подтверждает квалификацию', '━━━━━━━━━━━━━━', '████████████████', 'Курс: Натальная астрология', 'Часов: 240', '━━━━━━━━━━━━━━', '№ серт.: AST-██████']
                    },
                    'Видео-презентация': {
                      bg: 'bg-purple-50',
                      border: 'border-purple-200',
                      icon: '🎬',
                      lines: ['▶  ВИДЕО', 'Презентация астролога', '━━━━━━━━━━━━━━', 'Длительность: 3:42', 'Формат: MP4', '━━━━━━━━━━━━━━', 'Размер: 48.2 MB']
                    }
                  };
                  const style = docStyles[type] || {
                    bg: 'bg-slate-50',
                    border: 'border-slate-200',
                    icon: '📄',
                    lines: ['Документ', '━━━━━━━━━━━━━━', '████████████████']
                  };
                  return <Card key={idx} padding="sm" className={`${style.border} hover:shadow-md transition-all cursor-pointer group`} onClick={() => setViewingDoc({
                    type,
                    lines: style.lines,
                    icon: style.icon,
                    bg: style.bg
                  })} data-id="element-7664">
                          <div className={`h-40 ${style.bg} rounded-lg flex flex-col items-center justify-center mb-3 p-3 relative overflow-hidden`} data-id="element-7665">
                            <div className="text-3xl mb-2" data-id="element-7666">{style.icon}</div>
                            <div className="w-full space-y-1" data-id="element-7667">
                              {style.lines.map((line, li) => <div key={li} className="text-[8px] text-slate-400 text-center font-mono leading-tight truncate" data-id="element-7668">
                                  {line}
                                </div>)}
                            </div>
                            <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg" data-id="element-7669">
                              <div className="bg-white rounded-full p-2 shadow-lg" data-id="element-7670">
                                <Eye className="w-5 h-5 text-slate-700" data-id="element-7671" />
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-slate-900" data-id="element-7672">
                            {type}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7673">
                            Загружен 13 Окт 2024 ·{' '}
                            {type === 'Видео-презентация' ? 'MP4 · 48.2 MB' : 'PDF · 2.4 MB'}
                          </div>
                        </Card>;
                })}
                  </div>
                </div>
              </div>

              {/* Right Col: History */}
              <div className="w-full md:w-80 flex-shrink-0" data-id="element-7674">
                <Card padding="sm" className="border-slate-200" data-id="element-7675">
                  <h4 className="font-semibold text-slate-900 mb-4 text-sm flex items-center gap-2" data-id="element-7676">
                    <Clock className="w-4 h-4 text-slate-400" data-id="element-7677" /> История
                    проверки
                  </h4>
                  <div className="space-y-0" data-id="element-7678">
                    {previewData.history.map((h: {
                  date: string;
                  text: string;
                  actor: string;
                }, idx: number) => <div key={idx} className="flex gap-3 pb-4 relative" data-id="element-7679">
                          {idx < previewData.history.length - 1 && <div className="absolute left-[7px] top-4 w-px h-full bg-slate-200" data-id="element-7680" />}
                          <div className="w-4 h-4 rounded-full bg-indigo-100 border-2 border-indigo-400 flex-shrink-0 mt-0.5 relative z-10" data-id="element-7681" />
                          <div data-id="element-7682">
                            <div className="text-xs font-medium text-slate-900" data-id="element-7683">
                              {h.text}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7684">
                              {h.date} · {h.actor}
                            </div>
                          </div>
                        </div>)}
                  </div>
                </Card>

                {/* Quick Actions in Modal */}
                <div className="mt-4 space-y-2" data-id="element-7685">
                  <Button variant="primary" size="sm" fullWidth icon={<CheckCircle2 className="w-4 h-4" data-id="element-7687" />} onClick={() => {
                handleApprove(previewData);
                setPreviewData(null);
              }} data-id="element-7686">
                    Одобрить заявку
                  </Button>
                  <Button variant="outline" size="sm" fullWidth icon={<HelpCircle className="w-4 h-4" data-id="element-7689" />} onClick={() => {
                handleRequestInfo(previewData);
                setPreviewData(null);
              }} data-id="element-7688">
                    Запросить дополнение
                  </Button>
                  <Button variant="outline" size="sm" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" icon={<XCircle className="w-4 h-4" data-id="element-7691" />} onClick={() => {
                handleReject(previewData);
                setPreviewData(null);
              }} data-id="element-7690">
                    Отклонить заявку
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}